/* eslint-disable no-undef */
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import EditIcon from '../../../../assets/img/svg/edit-icon.svg'
import NoteIcon from '../../../../assets/img/svg/note-icon.svg'
import { propertySchema } from '../../../../util/validationSchema'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { addProperty, editProperty, getAllConsumables, getAllTasks, getPropertyByID } from '../../../../redux/slices/propertySlice'
import { SpinnerContext } from '../../../../context/spinnerContext'
import { acceptExtensions, defultPropertyValues, IMAGE_MODULE, TIMEZONE_OPTIONS } from '../../../../util/constant'
import EditProperty from './editproperty'
import { Accordion, Button, Form } from 'react-bootstrap'
import GetInForm from './getInForm'
import TasksForm from './tasksForm'
import ConsumableForm from './consumableForm'
import CustomModal from '../../../../component/customModal'
import ListofTask from '../../../../component/Property/listofTask'
import ListofConsumable from '../../../../component/Property/listofConsumable'
import DropzoneController from '../../../../component/dropzone'
import { errorToast, successToast } from '../../../../util/Toast/toast'
import UploadProperty from '../../../../component/Property/uploadProperty'
import axios from 'axios'
import { useLoadScript, StandaloneSearchBox } from '@react-google-maps/api'
import { getCityByCityName } from '../../../../redux/slices/citySlice'
import { debounce, getUserRole, hasAccess } from '../../../../helper/function'
import ICallForm from './iCallForm'

const AddEditProperty = () => {
  const { client_id, property_id } = useParams()
  const isEditMode = Boolean(property_id)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { getProperty, allTasks, allConsumables } = useSelector(({ property }) => property)
  const { changeLoader } = useContext(SpinnerContext)
  const [showListTaskModal, setShowListTaskModal] = useState(false)
  const toggleListTaskModal = () => setShowListTaskModal(!showListTaskModal)
  const [showListConsumableModal, setShowListConsumableModal] = useState(false)
  const toggleListConsumableModal = () => setShowListConsumableModal(!showListConsumableModal)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const toggleUploadModal = () => setShowUploadModal(!showUploadModal)
  const [isRefresh, setIsRefresh] = useState(false)
  const [address, setAddress] = useState('')
  const inputRef = useRef(null)
  const [isAboutToggle, setIsAboutToggle] = useState(false)
  const [accrdionActiveKey, setAccrdionActiveKey] = useState(null)
  const [activePopover, setActivePopover] = useState(null)

  // Load Google Maps script
  const disableIcal = useMemo(
    () => !getProperty?.client?.invoice_payment && !getProperty?.client?.is_card && !getProperty?.client?.is_ghost,
    [getProperty?.client]
  )
  const { isLoaded } = useLoadScript({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    libraries: ['places']
  })

  const handlePlacesChanged = (onChange) => {
    const places = inputRef?.current?.getPlaces()

    if (places?.length) {
      const place = places[0] // Get the first place
      const lat = place?.geometry?.location?.lat()
      const lng = place?.geometry?.location?.lng()
      const addressComponents = place?.address_components || []
      let city = watch('city')
      // If city is already selected, validate the address
      if (city) {
        const cityMatch = addressComponents.some((comp) => comp.long_name.toLowerCase() === city.toLowerCase())

        if (cityMatch) {
          updateAddressFields(place, lat, lng, onChange)
        } else {
          console.log('Error...')
        }
      } else {
        // If no city is selected, extract city and state
        extractAndSetLocationData(addressComponents, lat, lng, place, onChange)
      }
    } else {
      console.error('No places found')
      resetLocationFields()
    }
  }

  const updateAddressFields = (place, lat, lng, onChange) => {
    onChange(place?.formatted_address || '')
    setAddress(place?.formatted_address)
    setValue('location_lat', lat?.toString() || '')
    setValue('location_long', lng?.toString() || '')
  }

  const extractAndSetLocationData = (addressComponents, lat, lng, place, onChange) => {
    let cityFromPlace = ''
    let state = ''
    let zipcode = ''

    addressComponents.forEach((component) => {
      const types = component.types

      if (types.includes('locality')) {
        cityFromPlace = component.long_name
      }
      if (types.includes('administrative_area_level_1')) {
        state = component.short_name
      }
      if (types.includes('postal_code')) {
        zipcode = component.long_name
      }
    })

    updateAddressFields(place, lat, lng, onChange)
    setValue('city', cityFromPlace)
    setValue('state', state)
    setValue('zipcode', zipcode)
    trigger('city')
    trigger('state')
    trigger('zipcode')
  }

  const resetLocationFields = () => {
    setValue('city', '')
    setValue('state', '')
    setValue('zipcode', '')
  }

  const getCityAndStateByZipcode = async (zipcode) => {
    try {
      const response = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${zipcode}&key=${process.env.REACT_APP_GOOGLE_MAP_API_KEY}`
      )
      if (response.data.results.length > 0) {
        const addressComponents = response.data.results[0].address_components

        let city = ''
        let state = ''
        addressComponents?.forEach((component) => {
          if (component.types.includes('locality')) {
            city = component.long_name
          }
          if (component.types.includes('administrative_area_level_1')) {
            state = component.long_name
          }
        })
        if (city) {
          changeLoader(true)
          dispatch(getCityByCityName(city))
            .then(({ payload }) => {
              changeLoader(false)
              if (payload?.data?.status_code === 200) {
                setValue('city', city)
                setValue('state', state)
                trigger('city')
                trigger('state')
              } else {
                errorToast(payload?.data?.status_message || 'City not found!')
                setAddress('')
                setValue('city', '')
                setValue('state', '')
                setValue('address', '')
              }
            })
            .catch(() => {
              changeLoader(false)
              setValue('address', '')
            })
        }
      } else {
        setAddress('')
        setValue('city', '')
        setValue('state', '')
        setValue('address', '')
      }
    } catch (error) {
      setAddress('')
      setValue('city', '')
      setValue('state', '')
      setValue('address', '')
      trigger('city')
      trigger('state')
      console.error('Error fetching city and state', error)
    }
  }

  const debouncedGetCityAndState = useCallback(
    debounce((zipcode) => {
      getCityAndStateByZipcode(zipcode)
    }, 1000), // Adjust the debounce delay as needed (500ms in this case)
    []
  )

  const handleZipcodeChange = (e) => {
    const zipcode = e.target.value
    debouncedGetCityAndState(zipcode)
  }

  const handleInputChange = (e) => {
    setAddress(e.target.value)
  }

  const updatedPropertyValues = isEditMode
    ? {
        property_name: getProperty?.property_name || '',
        client_id: getProperty?.client?.client_id || '',
        number_of_rooms: getProperty?.number_of_rooms || '',
        number_of_bathroom: getProperty?.number_of_bathroom || '',
        square_foot: getProperty?.square_foot || '',
        other: getProperty?.other || '',
        zipcode: getProperty?.zipcode || '',
        address: getProperty?.address || '',
        state: getProperty?.state || '',
        city: getProperty?.city || '',
        location_lat: getProperty?.location_lat || '',
        location_long: getProperty?.location_long || '',
        get_in: getProperty?.get_in || '',
        get_in_instruction: getProperty?.get_in_instruction || '',
        laundry_code: getProperty?.laundry_code || '',
        storage_area_code: getProperty?.storage_area_code || '',
        propertyICalLink:
          getProperty?.propertyICalLink?.length > 0
            ? getProperty?.propertyICalLink.map((link) => ({
                property_cleaning_icallink_id: link?.property_cleaning_icallink_id,
                ical_label: link?.ical_label,
                ical_link: link?.ical_link
              }))
            : [{ property_cleaning_icallink_id: null, ical_label: '', ical_link: '' }],
        property_image: getProperty?.property_image || [],
        airbnb_listing_id: getProperty?.airbnb_listing_id || '',
        airbnb_bedrooms: getProperty?.airbnb_bedrooms || 0,
        airbnb_bathrooms: getProperty?.airbnb_bathrooms || 0,
        request_laundry: getProperty?.request_laundry || false,
        cleaning_on: getProperty?.cleaning_on || '',
        propertyTasks:
          getProperty?.propertyTasks?.length > 0
            ? getProperty?.propertyTasks.map((task) => ({
                property_cleaning_task_id: task?.property_cleaning_task_id || null,
                task: task?.task
              }))
            : [{ property_cleaning_task_id: null, task: '' }],
        propertyConsumables:
          getProperty?.propertyConsumables?.length > 0
            ? getProperty?.propertyConsumables.map((consumable) => ({
                property_cleaning_consumable_id: consumable?.property_cleaning_consumable_id || null,
                task: consumable?.task
              }))
            : [{ property_cleaning_consumable_id: null, task: '' }],
        timezone: getProperty?.timezone || ''
      }
    : defultPropertyValues

  // Initialize react-hook-form
  const methods = useForm({
    resolver: yupResolver(propertySchema),
    mode: 'onBlur',
    defaultValues: updatedPropertyValues
  })

  const {
    handleSubmit,
    control,
    register,
    watch,
    setValue,
    reset,
    trigger,
    clearErrors,
    formState: { errors }
  } = methods

  const checkboxOptionsForCleaningOn = []
  const selectedCleaningOnValue = watch('cleaning_on')

  const handleCleaningOnChange = (value) => {
    setValue(
      'cleaning_on',
      value === selectedCleaningOnValue ? (value === 'Cleaning on checkin' ? 'Cleaning on checkout' : 'Cleaning on checkin') : value
    )
  }

  useEffect(() => {
    if (isEditMode && getProperty) {
      reset(updatedPropertyValues)
    } else {
      reset(defultPropertyValues)
    }
  }, [getProperty, reset, isEditMode])

  useEffect(() => {
    setValue('client_id', client_id)
  }, [client_id])

  useEffect(() => {
    const fetchTasksAndConsumables = async () => {
      try {
        // Run both dispatches in parallel using Promise.all
        await Promise.all([dispatch(getAllTasks()), dispatch(getAllConsumables())])
      } catch (error) {
        console.error('Error fetching tasks and consumables:', error)
      }
    }
    fetchTasksAndConsumables()
  }, [])

  const mergeData = (previousData, newData, dataType) => {
    return [
      ...previousData,
      ...newData
        .filter((item) => item.task && !previousData.some((prevItem) => prevItem.task === item.task))
        .map((item) => ({
          [dataType]: item?.[dataType] || null,
          task: item?.task || ''
        }))
    ].filter((item) => item.task !== '')
  }

  const handleGetTasks = () => {
    changeLoader(true)
    dispatch(getAllTasks())
      .then(() => {
        const previousTasks = watch('propertyTasks') || []
        const mergedTasks = mergeData(previousTasks, allTasks, 'property_cleaning_task_id')
        setValue('propertyTasks', mergedTasks.length > 0 ? mergedTasks : [])
      })
      .catch((err) => {
        console.log('err :>> ', err)
      })
      .finally(() => changeLoader(false))
  }

  const handleGetConsumables = () => {
    changeLoader(true)
    dispatch(getAllConsumables())
      .then(() => {
        const previousConsumables = watch('propertyConsumables') || []
        const mergedConsumables = mergeData(previousConsumables, allConsumables, 'property_cleaning_consumable_id')
        setValue('propertyConsumables', mergedConsumables.length > 0 ? mergedConsumables : [])
      })
      .catch((err) => {
        console.log('err :>> ', err)
      })
      .finally(() => changeLoader(false))
  }

  useEffect(() => {
    if (property_id) {
      changeLoader(true)
      dispatch(getPropertyByID(property_id))
        .then(() => {
          changeLoader(false)
        })
        .catch(() => {
          changeLoader(false)
        })
    }
  }, [isRefresh])

  const handleUpload = () => {
    if (property_id) {
      changeLoader(true)
      const currentData = watch()
      const payload = {
        data: {
          ...currentData,
          property_image: currentData.property_image,
          property_name: currentData?.property_name?.trim(),
          square_foot: currentData?.square_foot?.toString(),
          laundry_code: currentData?.laundry_code?.trim(),
          storage_area_code: currentData?.storage_area_code?.trim(),
          propertyICalLink: currentData?.propertyICalLink?.filter((item) => item.ical_link && item.ical_label),
          propertyTasks: currentData?.propertyTasks?.filter((item) => item.task),
          propertyConsumables: currentData?.propertyConsumables?.filter((item) => item.task)
        },
        property_id: property_id
      }
      dispatch(editProperty(payload))
        .then(({ payload: responsePayload }) => {
          if (responsePayload?.status === 200) {
            setIsRefresh(true)
          } else {
            errorToast(responsePayload?.data?.status_message || 'Não foi possível salvar a propriedade.')
          }
          setShowUploadModal(false)
          changeLoader(false)
        })
        .catch(() => {
          changeLoader(false)
          setShowUploadModal(false)
        })
    }
  }

  useEffect(() => {
    const errorMapping = {
      about: errors.number_of_rooms || errors.number_of_bathroom || errors.square_foot,
      get_in: errors.get_in || errors.get_in_instruction || errors.laundry_code || errors.storage_area_code,
      ical: errors.propertyICalLink,
      task: errors.propertyTasks,
      consumables: errors.propertyConsumables
    }

    if (errorMapping[accrdionActiveKey]) return

    for (const [key, hasError] of Object.entries(errorMapping)) {
      if (hasError) {
        setAccrdionActiveKey(key)
        if (key === 'about' && !isAboutToggle) {
          setIsAboutToggle((prev) => !prev)
        }
        break
      } else {
        setAccrdionActiveKey(null)
      }
    }
  }, [errors])

  const onSubmit = async (data) => {
    setIsRefresh(false)
    changeLoader(true)
    const _data = {
      ...data,
      property_name: data?.property_name.trim(),
      square_foot: data?.square_foot.toString(),
      laundry_code: data?.laundry_code.trim(),
      storage_area_code: data?.storage_area_code.trim(),
      propertyICalLink: data.propertyICalLink?.filter((item) => item.ical_link && item.ical_label),
      propertyTasks: data.propertyTasks?.filter((item) => item.task),
      propertyConsumables: data.propertyConsumables?.filter((item) => item.task)
    }
    if (property_id) {
      const payload = {
        data: _data,
        property_id: property_id
      }
      setAccrdionActiveKey(null)
      dispatch(editProperty(payload)).then(({ payload, error }) => {
        if (payload?.status === 200) {
          changeLoader(false)
          isAboutToggle && setIsAboutToggle(!isAboutToggle)
          successToast(payload?.data?.status_message || 'Success!')
          setIsRefresh(true)
          // navigate(`/clients/clientInfo/${client_id}`)
        } else {
          errorToast(payload?.data?.status_message || error?.message)
          changeLoader(false)
        }
      })
    } else {
      dispatch(addProperty(_data)).then(({ payload, error }) => {
        if (payload?.status === 200) {
          changeLoader(false)
          successToast(payload?.data?.status_message || 'Success!')
          navigate(`/clients/clientInfo/${client_id}`)
        } else {
          errorToast(payload?.data?.status_message || error?.message)
          changeLoader(false)
        }
      })
    }
  }
  const onAccordionChange = (key) => {
    setActivePopover(null)
    setAccrdionActiveKey((prev) => (prev !== key ? key : null))
  }

  return (
    <>
      <div className="page-wrapper">
        {/* New Code Start */}
        <div className="content">
          {/* Edit Propety Code Start */}
          {isEditMode && (
            <>
              {' '}
              <div className="heading_header_div">
                <h4>{getProperty?.property_name}</h4>
                <div className="d-flex align-items-center gap-2">
                  <img className="img-fluid" src={EditIcon} alt="Icon" />
                  <Link className="links_text" onClick={() => toggleUploadModal()}>
                    Edit
                  </Link>
                </div>
              </div>
              <EditProperty
                getProperty={getProperty}
                property_image={watch('property_image')}
                errors={errors}
                control={control}
                trigger={trigger}
                register={register}
                watch={watch}
                setValue={setValue}
                setIsRefresh={setIsRefresh}
                setIsAboutToggle={setIsAboutToggle}
                isAboutToggle={isAboutToggle}
                onAccordionChange={onAccordionChange}
                accrdionActiveKey={accrdionActiveKey}
              />
            </>
          )}
          {/* Edit Propety Code End */}

          {!isEditMode && <h4 className="common_heading_text">New Property</h4>}
          <div className={!isEditMode && 'card'}>
            <div className={!isEditMode && 'card-body card_body_div'}>
              {!isEditMode && <h5 className="card_small_heading">Add Property Details</h5>}
              <>
                <FormProvider {...methods}>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <>
                      {/* <div className="row"> */}
                      {!isEditMode && (
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>
                                Number of Rooms<span className="text-danger"> *</span>
                              </label>
                              <Controller
                                name="number_of_rooms"
                                control={control}
                                render={({ field: { value, onChange }, fieldState }) => (
                                  <>
                                    <input
                                      autoFocus
                                      className="form-control"
                                      value={value}
                                      type="number"
                                      placeholder="Enter Number of Rooms"
                                      onChange={(val) => {
                                        onChange(val)
                                        trigger('number_of_rooms')
                                      }}
                                    />
                                    <small>{fieldState?.error?.message}</small>
                                  </>
                                )}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>
                                Number of Bathrooms<span className="text-danger"> *</span>
                              </label>
                              <Controller
                                name="number_of_bathroom"
                                control={control}
                                render={({ field: { value, onChange }, fieldState }) => (
                                  <>
                                    <input
                                      className="form-control"
                                      value={value}
                                      type="number"
                                      placeholder="Enter Number of Bathrooms"
                                      onChange={(val) => {
                                        onChange(val)
                                        trigger('number_of_bathroom')
                                      }}
                                    />
                                    <small>{fieldState?.error?.message}</small>
                                  </>
                                )}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>
                                Square Feet<span className="text-danger"> *</span>
                              </label>
                              <Controller
                                name="square_foot"
                                control={control}
                                render={({ field: { value, onChange }, fieldState }) => (
                                  <>
                                    <input
                                      className="form-control"
                                      value={value}
                                      type="number"
                                      placeholder="Enter Square Feet"
                                      onChange={(val) => {
                                        onChange(val)
                                        trigger('square_foot')
                                      }}
                                    />
                                    <small>{fieldState?.error?.message}</small>
                                  </>
                                )}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Other</label>
                              <Controller
                                name="other"
                                control={control}
                                render={({ field: { value, onChange }, fieldState }) => (
                                  <>
                                    <input
                                      className="form-control"
                                      value={value}
                                      type="text"
                                      placeholder="Enter Other"
                                      onChange={(val) => {
                                        onChange(val)
                                        trigger('other')
                                      }}
                                    />
                                    <small>{fieldState?.error?.message}</small>
                                  </>
                                )}
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="form-group">
                              <label>
                                Property Name<span className="text-danger"> *</span>
                              </label>
                              <Controller
                                name="property_name"
                                control={control}
                                render={({ field: { value, onChange }, fieldState }) => (
                                  <>
                                    <input
                                      className="form-control"
                                      value={value}
                                      type="text"
                                      placeholder="Enter Property Name"
                                      onChange={(val) => {
                                        onChange(val)
                                        trigger('property_name')
                                      }}
                                    />
                                    <small>{fieldState?.error?.message}</small>
                                  </>
                                )}
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="form-group">
                              <label>
                                Address<span className="text-danger"> *</span>
                              </label>
                              <Controller
                                name="address"
                                control={control}
                                render={({ field: { onChange } }) => (
                                  <>
                                    {isLoaded && (
                                      <StandaloneSearchBox
                                        onLoad={(ref) => (inputRef.current = ref)}
                                        onPlacesChanged={() => handlePlacesChanged(onChange)}
                                      >
                                        <input
                                          className="form-control"
                                          type="text"
                                          placeholder="Enter Address"
                                          value={address}
                                          onChange={(e) => {
                                            handleInputChange(e)
                                            onChange(e.target.value)
                                          }}
                                        />
                                      </StandaloneSearchBox>
                                    )}
                                  </>
                                )}
                                defaultValue=""
                              />

                              <small>{errors?.address?.message}</small>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>
                                Zipcode<span className="text-danger"> *</span>
                              </label>
                              <Controller
                                name="zipcode"
                                control={control}
                                render={({ field: { value, onChange }, fieldState }) => (
                                  <>
                                    <input
                                      className="form-control"
                                      value={value}
                                      type="text"
                                      placeholder="Enter Zipcode"
                                      onChange={(val) => {
                                        onChange(val)
                                        trigger('zipcode')
                                        handleZipcodeChange(val)
                                      }}
                                    />
                                    <small>{fieldState?.error?.message}</small>
                                  </>
                                )}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>
                                City<span className="text-danger"> *</span>
                              </label>
                              <Controller
                                name="city"
                                control={control}
                                render={({ field: { value, onChange }, fieldState }) => (
                                  <>
                                    <input
                                      className="form-control"
                                      value={value}
                                      readOnly
                                      type="text"
                                      placeholder="Enter City"
                                      onChange={(val) => {
                                        onChange(val)
                                        trigger('city')
                                      }}
                                    />
                                    <small>{fieldState?.error?.message}</small>
                                  </>
                                )}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>
                                State<span className="text-danger"> *</span>
                              </label>
                              <Controller
                                name="state"
                                control={control}
                                render={({ field: { value, onChange }, fieldState }) => (
                                  <>
                                    <input
                                      className="form-control"
                                      value={value}
                                      readOnly
                                      type="text"
                                      placeholder="Enter State"
                                      onChange={(val) => {
                                        onChange(val)
                                        setAccrdionActiveKey('get_in')
                                        trigger('state')
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Tab') {
                                          setAccrdionActiveKey('get_in')
                                        }
                                      }}
                                    />
                                    <small>{fieldState?.error?.message}</small>
                                  </>
                                )}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label>Timezone</label>
                              <Controller
                                name="timezone"
                                control={control}
                                render={({ field: { value, onChange }, fieldState }) => (
                                  <>
                                    <select
                                      className="form-control form-select"
                                      value={value}
                                      onChange={(e) => {
                                        onChange(e.target.value)
                                        trigger('timezone')
                                      }}
                                    >
                                      {TIMEZONE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                    <small>{fieldState?.error?.message}</small>
                                  </>
                                )}
                              />
                            </div>
                          </div>

                          <div className="col-md-12">
                            <div className="form-group">
                              <label>Upload Your Property Photos</label>
                              <DropzoneController
                                name="property_image"
                                maxFiles={20}
                                acceptExtensions={acceptExtensions}
                                control={control}
                                files={watch('property_image')}
                                module={IMAGE_MODULE.CLIENT}
                                isAddMode={isEditMode ? false : true}
                              />
                            </div>
                          </div>

                          {checkboxOptionsForCleaningOn.map((option, index) => (
                            <div className="col-md-6 mb-3" key={index}>
                              <Controller
                                name="cleaning_on"
                                control={control}
                                checked={selectedCleaningOnValue}
                                defaultValue={false}
                                render={({ field, fieldState }) => (
                                  <>
                                    <Form.Check
                                      type="switch"
                                      id="custom-switch"
                                      label={option}
                                      {...field}
                                      checked={field.value === option} // ensures checked state is bound to value
                                      onChange={() => {
                                        handleCleaningOnChange(option)
                                        trigger('cleaning_on')
                                      }}
                                    />
                                    <small>{fieldState?.error?.message}</small>
                                  </>
                                )}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Accordion Design Start */}
                      <div className="accordion_main_div">
                        <Accordion activeKey={accrdionActiveKey}>
                          <Accordion.Item eventKey="get_in">
                            <Accordion.Header onClick={() => onAccordionChange('get_in')}>Get In</Accordion.Header>
                            <Accordion.Body>
                              <GetInForm control={control} errors={errors} register={register} watch={watch} setValue={setValue} />
                            </Accordion.Body>
                          </Accordion.Item>

                          {isEditMode && (hasAccess('iCallAccess', getUserRole()) || getProperty?.propertyICalLink?.length > 0) && (
                            <Accordion.Item eventKey="ical">
                              <Accordion.Header onClick={() => onAccordionChange('ical')}>ICAL</Accordion.Header>
                              <Accordion.Body>
                                <ICallForm setActivePopover={setActivePopover} activePopover={activePopover} disableIcal={disableIcal} />
                                {/* <GetInForm control={control} errors={errors} register={register} watch={watch} setValue={setValue} /> */}
                              </Accordion.Body>
                            </Accordion.Item>
                          )}
                          <Accordion.Item eventKey="task">
                            <Accordion.Header onClick={() => onAccordionChange('task')}>Task</Accordion.Header>
                            <Accordion.Body>
                              <div className="head_checklist">
                                <h6 className="acc_title">
                                  We have a{' '}
                                  <Button className="hightlight_box" onClick={() => toggleListTaskModal()}>
                                    <img src={NoteIcon} alt="upload" width={16} /> List of Task
                                  </Button>{' '}
                                  for your cleaner.
                                </h6>
                                <Button
                                  className="Checklist_btn white_icon_with_btn_svg d-flex align-items-center gap-1"
                                  onClick={() => {
                                    handleGetTasks()
                                    setValue(
                                      'propertyTasks',
                                      allTasks?.length > 0
                                        ? allTasks.map((task) => ({
                                            property_cleaning_task_id: task?.property_cleaning_task_id || null,
                                            task: task?.task
                                          }))
                                        : [{ property_cleaning_task_id: null, task: '' }]
                                    )
                                    clearErrors('propertyTasks')
                                  }}
                                >
                                  Import Checklist
                                  <svg width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      d="M11.2519 4.84253L7.79683 8.10253C7.38878 8.48753 6.72108 8.48753 6.31303 8.10253L2.85791 4.84253"
                                      stroke="white"
                                      strokeWidth="1.68195"
                                      strokeMiterlimit="10"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </Button>
                              </div>
                              <p className="accordion_sec_title mb-3">
                                Add your own checklist or special request if you want. If our list is enough, you can skip this part and click ‘save’.
                              </p>
                              <TasksForm error={errors?.propertyTasks} />
                            </Accordion.Body>
                          </Accordion.Item>
                          <Accordion.Item eventKey="consumables">
                            <Accordion.Header onClick={() => onAccordionChange('consumables')}>Consumables</Accordion.Header>
                            <Accordion.Body>
                              <div className="head_checklist">
                                <h6 className="accordion_sec_title">
                                  {' '}
                                  Missing any consumables that you provide to your guest from our{' '}
                                  <Button className="hightlight_box" onClick={() => toggleListConsumableModal()}>
                                    <img src={NoteIcon} alt="upload" width={16} /> Default List
                                  </Button>{' '}
                                  ? Just add them here to help us track the inventory. Again, this is optional. You can skip it and click ‘save’.
                                </h6>
                                <Button
                                  className="Checklist_btn white_icon_with_btn_svg d-flex align-items-center gap-1"
                                  onClick={() => {
                                    handleGetConsumables()
                                    setValue(
                                      'propertyConsumables',
                                      allConsumables?.length > 0
                                        ? allConsumables.map((task) => ({
                                            property_cleaning_consumable_id: task?.property_cleaning_consumable_id || null,
                                            task: task?.task
                                          }))
                                        : [{ property_cleaning_consumable_id: null, task: '' }]
                                    )
                                    clearErrors('propertyConsumables')
                                  }}
                                >
                                  Import Checklist
                                  <svg width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      d="M11.2519 4.84253L7.79683 8.10253C7.38878 8.48753 6.72108 8.48753 6.31303 8.10253L2.85791 4.84253"
                                      stroke="white"
                                      strokeWidth="1.68195"
                                      strokeMiterlimit="10"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </Button>
                              </div>
                              <p className="accordion_sec_title mb-3">
                                Add your own checklist or special request if you want. If our list is enough, you can skip this part and click ‘save’.
                              </p>
                              <ConsumableForm />
                            </Accordion.Body>
                          </Accordion.Item>
                        </Accordion>
                      </div>
                      {/* Accordion Design End */}
                      {/* </div> */}

                      <div className="text-center mt-4">
                        <button className="btn btn-lg btn-block w-80 btn-primary" type="submit">
                          Submit
                        </button>
                      </div>
                      {showUploadModal && (
                        <CustomModal isOpen={showUploadModal} toggle={toggleUploadModal} isTitle={false} size="lg">
                          <div className="p-3">
                            <UploadProperty watch={watch} control={control} />
                            <div className="multi_btn_div">
                              <button
                                type="button"
                                className="btn common-outline-btn w-100"
                                onClick={() => {
                                  setShowUploadModal(false)
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                className="btn btn-primary w-100"
                                onClick={() => {
                                  handleUpload()
                                }}
                              >
                                Upload
                              </button>
                            </div>
                          </div>
                        </CustomModal>
                      )}
                    </>
                  </form>
                </FormProvider>
              </>
            </div>
          </div>
        </div>
        {/* New Code End */}
      </div>
      {showListTaskModal && (
        <CustomModal isOpen={showListTaskModal} toggle={toggleListTaskModal} title={`List of Tasks`} size="md">
          <ListofTask />
        </CustomModal>
      )}
      {showListConsumableModal && (
        <CustomModal isOpen={showListConsumableModal} toggle={toggleListConsumableModal} title={`List of Consumables`} size="md">
          <ListofConsumable />
        </CustomModal>
      )}
    </>
  )
}

export default AddEditProperty
